/**
 * roles.repository.ts — 사업부별 부서(roles) 구독 + 저장(12 §16-2).
 *
 * Firestore: tenants/{t}/units/{biz}/roles/{id} — { label|name, permissions|perms[], allMenus, tenant_assignable }
 *   (필드명 양쪽 fallback). 같은 id 라도 사업부마다 권한이 따로. 루트 roles/{id} 는 되돌리기용 보관 — 읽지 않는다.
 * rules: read = 활성 직원, write = 전체 admin 만.
 * 루트(중앙 옛 자료) 권한·계정 부서(claims role)의 기준 = 중앙 레미콘(ROOT_ROLE_UNIT) — rules rootRoleDoc 과 같음.
 */
import { collection, doc, onSnapshot, serverTimestamp, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";
import type { Role, RoleHomeLayout } from "@/domain/user";
import type { BizKey, TenantKey } from "@/domain/space";

function parseHomeWidgets(v: unknown): Record<string, boolean> | undefined {
  if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
  const out: Record<string, boolean> = {};
  for (const [k, b] of Object.entries(v as Record<string, unknown>)) if (typeof b === "boolean") out[k] = b;
  return out;
}

export function parseHomeLayout(v: unknown): RoleHomeLayout | null {
  if (!v || typeof v !== "object") return null;
  const x = v as Record<string, unknown>;
  if (!Array.isArray(x.layout)) return null;
  const ts = x.updated_at as { toDate?: () => Date } | undefined;
  return {
    layout: x.layout as Record<string, unknown>[],
    version: typeof x.version === "number" ? x.version : 0,
    applyVersion: typeof x.apply_version === "number" ? x.apply_version : 0,
    updatedAt: ts && typeof ts.toDate === "function" ? ts.toDate() : null,
  };
}

const ROLE_ORDER = ["admin", "mgmt", "accounting", "sales", "quality", "production", "driver", "cafeteria"];

export interface RoleUnit { tenant: TenantKey; biz: BizKey }
export const ROOT_ROLE_UNIT: RoleUnit = { tenant: "joongang", biz: "rmc" };

const rolesCol = (u: RoleUnit) => collection(db, "tenants", u.tenant, "units", u.biz, "roles");
const roleDoc = (u: RoleUnit, id: string) => doc(db, "tenants", u.tenant, "units", u.biz, "roles", id);

export const rolesRepo = {
  watch(unit: RoleUnit, cb: (rows: Role[]) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      rolesCol(unit),
      (snap) => {
        const rows = snap.docs.map((d) => {
          const x = d.data();
          return {
            id: d.id,
            label: (typeof x.label === "string" && x.label) || (typeof x.name === "string" && x.name) || d.id,
            permissions: Array.isArray(x.permissions) ? x.permissions : Array.isArray(x.perms) ? x.perms : [],
            allMenus: x.allMenus === true,
            tenantAssignable: x.tenant_assignable === true,
            homeWidgets: parseHomeWidgets(x.home_widgets),
            homeLayout: parseHomeLayout(x.home_layout),
          } satisfies Role;
        });
        // admin → 부서 7개(부서별-권한 §1 순서) → 그 밖의 옛 그룹은 이름순
        const rank = (id: string) => {
          const i = ROLE_ORDER.indexOf(id);
          return i < 0 ? ROLE_ORDER.length : i;
        };
        rows.sort((a, b) => rank(a.id) - rank(b.id) || a.label.localeCompare(b.label, "ko"));
        cb(rows);
      },
      onError,
    );
  },

  /**
   * 부서 생성/수정 — label/permissions 저장 (legacy name/perms도 같이 써서 호환 유지). tenant_assignable·home_layout 은 건드리지 않음.
   * home_widgets 는 role.homeWidgets 가 있을 때만 통째로 교체(그룹 편집기). 알림 모달처럼 안 넘기면 그대로.
   */
  async save(unit: RoleUnit, role: Role): Promise<void> {
    await setDoc(
      roleDoc(unit, role.id),
      {
        label: role.label,
        name: role.label,
        permissions: role.permissions,
        perms: role.permissions,
        allMenus: role.allMenus === true,
        ...(role.homeWidgets ? { home_widgets: role.homeWidgets } : {}),
        updated_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /**
   * 부서 기본 홈 배치 저장(관리자, 홈 편집 모드 "그룹 기본 배치"). version 은 매번 1 증가.
   * apply = 구성원의 현재 배치도 다음 접속 때 이 배치로 바꾼다(apply_version 을 새 version 으로).
   * 아니면 "기본 배치" 복원·첫 진입 때만 쓰인다.
   */
  async saveHomeLayout(unit: RoleUnit, id: string, layout: Record<string, unknown>[], apply: boolean, prev: RoleHomeLayout | null | undefined): Promise<void> {
    const version = (prev?.version ?? 0) + 1;
    // Firestore 는 undefined 값을 거부 — JSON 라운드트립으로 제거(dashboard_prefs 와 같은 이유)
    const clean = JSON.parse(JSON.stringify(layout)) as Record<string, unknown>[];
    await setDoc(
      roleDoc(unit, id),
      {
        home_layout: {
          layout: clean,
          version,
          apply_version: apply ? version : (prev?.applyVersion ?? 0),
          updated_at: serverTimestamp(),
        },
        updated_at: serverTimestamp(),
      },
      { merge: true },
    );
  },

  /** 법인 관리자가 배정할 수 있는 부서 표시(tenant_assignable) — rules·함수가 읽는 필드 */
  async setTenantAssignable(unit: RoleUnit, id: string, value: boolean): Promise<void> {
    await setDoc(roleDoc(unit, id), { tenant_assignable: value, updated_at: serverTimestamp() }, { merge: true });
  },

  async remove(unit: RoleUnit, id: string): Promise<void> {
    await deleteDoc(roleDoc(unit, id));
  },
};
